const post = {
  slug: 'designing-apis-you-wont-hate-in-a-year',
  number: 15,
  title: "Designing APIs You Won't Hate in a Year",
  date: '2025-10-27',
  readTime: '7 min read',
  views: 38,
  category: 'backend',
  tags: ['Backend', 'APIs', 'System Design'],
  excerpt:
    'An API is a promise you have to keep for a long time. A few habits, decided early, are the difference between one you are happy to live with and one you quietly dread touching.',
  cover: null,
  content: `
The APIs I regret are never the ones that were hard to build. They are the ones that were easy to build and impossible to change. A field named badly that a dozen clients now depend on. A response shape that leaked the database structure, so I could not touch the schema without breaking everyone. None of those were performance problems or clever bugs. They were design decisions made on a Tuesday that I had to live with for two years.

An API is a promise. Once something depends on it, you cannot freely change it without breaking that something. This is what makes API design different from most code, where you can refactor behind your own walls all day. The interface is the one part you have to keep, so a few decisions made early decide whether you end up with an API you are happy to maintain or one you dread.

## Be consistent before you are clever

The most valuable property an API can have is predictability. If one endpoint returns a created record and another returns just an id, if one names a field createdAt and another created_date, every consumer has to learn each endpoint as its own special case. Consistency means that once someone learns one corner of your API, they can correctly guess the rest.

Pick conventions for naming, for structure, for how you return collections and errors, and then apply them everywhere, even when a small inconsistency would be marginally more convenient in one spot. Boring and consistent beats clever and surprising every time, because surprise is exactly what costs your consumers hours.

## Design your errors as carefully as your successes

It is easy to lavish attention on the happy path and treat errors as an afterthought. But consumers spend a large fraction of their time handling your errors, and a vague failure is genuinely hard to build against.

A good error tells the caller what went wrong, and where, in a consistent shape they can rely on.

\`\`\`json
{
  "error": "validation_failed",
  "message": "amount must be greater than zero",
  "field": "amount"
}
\`\`\`

Use status codes for what they actually mean. A client mistake is not a server error, and conflating the two sends people debugging in the wrong direction for an afternoon. A caller that can read your errors reliably is a caller that fixes its own bugs instead of filing a ticket against yours.

## Plan for change from day one

You will need to change the API. New fields, new behaviour, occasionally a different shape entirely. The trick is to make change additive whenever you can. Adding a new optional field breaks nobody. Removing a field, renaming one, or changing the meaning of an existing one breaks everybody who depended on the old behaviour.

\`\`\`text
Safe:    add a new optional field
Safe:    add a new endpoint
Breaking: remove or rename a field
Breaking: change the meaning or type of an existing field
\`\`\`

When a breaking change is truly unavoidable, that is what versioning is for, so existing consumers keep working on the old version while new ones adopt the new one. Designing as if nothing will ever change is precisely how you end up unable to change anything without an incident.

## Do not leak your database

A tempting shortcut is to return your database rows straight out of the API. It is fast to build, and it quietly chains your public interface to your internal schema. The day you want to rename a column or restructure a table, you cannot, because the whole world is reading the old shape through your endpoints.

Keep a deliberate boundary between how you store data and how you expose it. Map from one to the other on the way out. The small upfront cost of that mapping buys you the freedom to evolve your storage without renegotiating your contract with every consumer, which is a trade you will be grateful for within the year.

## The throughline, and a checklist

Good API design is mostly empathy for the next person, who is very often you, six months from now, trying to change something. Before I call an API design done:

- Are naming and structure consistent across every endpoint?
- Do errors have a consistent shape, a useful message, and correct status codes?
- Is every planned change additive, with versioning reserved for genuine breaks?
- Is there a boundary between the storage shape and the exposed shape?

None of it is fancy, and all of it is the difference between an interface that ages gracefully and one that becomes the thing nobody on the team wants to touch.
`,
};

export default post;
